import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { StatCard } from "@/components/data-display/stat-card";
import { TableState } from "@/components/data-display/table-state";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { InfoBanner } from "@/components/ui/banner";
import { buttonVariants } from "@/components/ui/button";
import { Table, TableWrapper } from "@/components/ui/table";
import { WipHint } from "@/components/ui/wip-hint";
import { SectionHeading } from "@/features/treasury/treasury-wip";

/**
 * `/treasury/staking` — Staking Positions.
 *
 * Layout ported from `.design-ref/prototype/app/(enterprise-wallet)/treasury/staking/page.tsx`
 * (the `StakingPositions` export in `features/treasury/treasury.tsx`): the back link,
 * the header + operator/last-sync line, the validator-alert banner, the five-tile
 * metric row, and the positions table with the `+ Create Staking Intent` action.
 *
 * No staking backend exists in this environment (context/page_pipeline/BOARD.md
 * wave 4, context/page_pipeline/BACKEND_GAPS.md): positions, validator health and
 * reward accrual were all fabricated before this page was deferred
 * (page-delivery-pipeline.md G6, 2026-09-23). This is a **layout-only** port: the
 * prototype's `STK_POSITIONS` / `STK_OVERVIEW_METRICS` sample data are not copied,
 * and neither is its `deferTo(...)` toast, which announced work it never did.
 * Every figure renders the unavailable marker, the table renders a designed empty
 * state, and the two actions that have a real route stay as links. See report.md
 * → BACKEND GAPS.
 */

/** Designer's `BackLink`, matching `features/treasury/treasury-ui.tsx` exactly. */
function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted transition-colors hover:text-primary"
    >
      <ArrowLeft aria-hidden className="size-3.5" /> {children}
    </Link>
  );
}

/** Table header cells, matching the designer's `th` classes exactly. */
const TH = "px-4 py-2.5";

export default function TreasuryStakingPage() {
  return (
    <div>
      <BackLink to="/treasury">Back to Treasury Home</BackLink>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[24px] font-bold leading-tight tracking-tight text-ink">Staking Positions</h1>
            <WipHint />
          </div>
          <p className="mt-1 text-sm text-muted">
            Active staking positions across validators, staking rewards, and unbonding status.
          </p>
          {/* The designer's operator / last-sync line: both values come from the missing backend. */}
          <p className="mt-1 text-xs text-muted">
            Operator: <UnavailableValue /> · Last sync: <UnavailableValue />
          </p>
        </div>
      </div>

      <div role="status" className="mb-5">
        <p className="text-sm text-muted">Staking is not available in this environment yet</p>
        <p className="text-xs text-muted">
          Staking positions, validator performance, and reward accrual have no backend in this
          environment yet. This page will show real positions once the staking domain is built.
        </p>
      </div>

      {/*
        The designer's banner announced a specific validator timeout and linked to
        the incident's evidence. No validator is monitored here, so naming one (or
        rendering the claim in the danger tone) would report an incident that never
        happened: the banner keeps its slot and drops the claim.
      */}
      <InfoBanner variant="info">
        <strong>Validator Alert:</strong> <UnavailableValue /> — validator health is not monitored in this
        environment yet.
      </InfoBanner>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
        {/* The designer toned these success/warning; an unknown value has no valence, so all five
            render untinted next to the shared "not available" marker. */}
        <StatCard value="" label="Total Staked Value" hint="Not available yet" />
        <StatCard value="" label="Active Positions" hint="Not available yet" />
        <StatCard value="" label="Pending Activation" hint="Not available yet" />
        <StatCard value="" label="Unbonding" hint="Not available yet" />
        <StatCard value="" label="Total Rewards Accrued" hint="Not available yet" />
      </div>

      <SectionHeading
        action={
          <Link to="/treasury/staking/new" className={buttonVariants({ size: "md" })}>
            + Create Staking Intent
          </Link>
        }
      >
        Staking Positions
      </SectionHeading>

      <TableWrapper>
        <Table className="dense">
          <thead>
            <tr className="border-b border-line bg-bg text-left text-xs font-semibold text-muted">
              <th className={TH}>ID</th>
              <th className={TH}>Asset</th>
              <th className={TH}>Network</th>
              <th className={TH}>Wallet</th>
              <th className={TH}>Validator / Route</th>
              <th className={TH}>Amount</th>
              <th className={TH}>Status</th>
              <th className={TH}>APY</th>
              <th className={`${TH} text-right`}>Action</th>
            </tr>
          </thead>
          <tbody>
            <TableState
              variant="empty"
              colSpan={9}
              title="No staking positions"
              description="Staking positions and their validator routes come from the staking backend, which does not exist in this environment yet."
            />
          </tbody>
        </Table>
      </TableWrapper>
    </div>
  );
}
